'use client';

import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { PrimaryButton } from '@/components/platform/buttons';

interface SuccessModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  message?: string | React.ReactNode;
  buttonText?: string;
  showIcon?: boolean;
}

export default function SuccessModal({
  open,
  onClose,
  title,
  message,
  buttonText = 'Close',
  showIcon = true,
}: SuccessModalProps) {
  const { darkMode } = useDarkMode();

  return (
    <Dialog open={open} onClose={onClose} className="relative z-[60]">
      <DialogBackdrop
        transition
        className={`fixed inset-0 backdrop-blur-[2px] transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in ${
          darkMode ? 'bg-gray-900/75' : 'bg-gray-500/75'
        }`}
      />

      <div className="fixed inset-0 z-[60] w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className={`relative transform overflow-hidden rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-sm sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95 ${
              darkMode ? 'border border-white/10 bg-gray-800' : 'bg-white'
            }`}
          >
            <div>
              {showIcon && (
                <div
                  className={`mx-auto flex size-12 items-center justify-center rounded-full ${
                    darkMode ? 'bg-green-900/20' : 'bg-green-100'
                  }`}
                >
                  <CheckIcon
                    aria-hidden="true"
                    className={`size-6 ${darkMode ? 'text-green-400' : 'text-green-600'}`}
                  />
                </div>
              )}
              <div className={`text-center ${showIcon ? 'mt-3 sm:mt-5' : ''}`}>
                <DialogTitle
                  as="h3"
                  className={`text-base font-semibold ${
                    darkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}
                >
                  {title}
                </DialogTitle>
                {message && (
                  <div className="mt-2">
                    {typeof message === 'string' ? (
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {message}
                      </p>
                    ) : (
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {message}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-5 sm:mt-6">
              <PrimaryButton onClick={onClose} className="w-full">
                {buttonText}
              </PrimaryButton>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
